import Dexie, { type Table } from 'dexie';
import {
  summarizeRecipe,
  type CatalogIngredient,
  type CookSession,
  type ManualItem,
  type MealSlot,
  type PlanMember,
  type PlanEntry,
  type Recipe,
  type RecipeSummary,
  type ShoppingCheck,
  type ShoppingPrefs,
  type ShoppingRecipe,
  type SyncLine,
  type SyncRecipe,
  type SyncResponse,
} from '@dishly/shared';
import { rebaseOutbox, type QueuedOp } from './outbox.ts';

interface Meta {
  key: 'cursor' | 'owner';
  value: string | number;
}

export type StoredShoppingPrefs = ShoppingPrefs & { key: 'shopping' };

/** A photo of a cooking-history entry taken here and not uploaded yet (offline). */
export interface PendingPhoto {
  sessionId: string;
  blob: Blob;
  addedAt: string;
}

/**
 * The device mirror (PLAN.md §9, ADR 0006): recipes, their lines, the catalog, meal slots, the
 * plan and the shopping state as pulled from `/api/sync`. Read when the server can't be reached;
 * only sync writes to it.
 */
export class Mirror extends Dexie {
  recipes!: Table<SyncRecipe, string>;
  lines!: Table<SyncLine, string>;
  ingredients!: Table<CatalogIngredient, string>;
  slots!: Table<MealSlot, string>;
  cookSessions!: Table<CookSession, string>;
  planEntries!: Table<PlanEntry, string>;
  shoppingChecks!: Table<ShoppingCheck, string>;
  manualItems!: Table<ManualItem, string>;
  /** Recipes on the shopping list without a day. */
  shoppingRecipes!: Table<ShoppingRecipe, string>;
  prefs!: Table<StoredShoppingPrefs, string>;
  members!: Table<PlanMember, string>;
  /** Plan and shopping changes made here that the server doesn't have yet. */
  outbox!: Table<QueuedOp, number>;
  /** Cooking-history photos waiting for a connection. */
  photos!: Table<PendingPhoto, string>;
  meta!: Table<Meta, string>;

  constructor(name = 'dishly') {
    super(name);
    this.version(1).stores({
      recipes: 'id',
      lines: 'id, recipeId',
      ingredients: 'id',
      meta: 'key',
    });
    // P2: the plan and the shopping list. The cursor starts over so they pull in full.
    this.version(2)
      .stores({
        slots: 'id',
        cookSessions: 'id, date, recipeId',
        planEntries: 'id, date, cookSessionId',
        shoppingChecks: 'id, rangeStart',
        manualItems: 'id',
        prefs: 'key',
      })
      .upgrade((tx) => tx.table('meta').delete('cursor'));
    this.version(3).stores({ outbox: '++seq' });
    this.version(4)
      .stores({ members: 'id' })
      .upgrade((tx) => tx.table('meta').delete('cursor'));
    // P4.01: recipes carry their prep-ahead; none until they are pulled again in full.
    this.version(5)
      .stores({})
      .upgrade(async (tx) => {
        await tx
          .table('recipes')
          .toCollection()
          .modify((r: { prepAhead?: string[] }) => {
            r.prepAhead ??= [];
          });
        await tx.table('meta').delete('cursor');
      });
    // Recipes on the shopping list without planning them; pulled in full.
    this.version(6)
      .stores({ shoppingRecipes: 'id, recipeId' })
      .upgrade((tx) => tx.table('meta').delete('cursor'));
    // Cooking history: sessions carry a photo (pulled in full); photos taken offline wait here.
    this.version(7)
      .stores({ photos: 'sessionId, addedAt' })
      .upgrade((tx) => tx.table('meta').delete('cursor'));
  }

  /** Every table sync writes, for one transaction. */
  get synced() {
    return [
      this.recipes,
      this.lines,
      this.ingredients,
      this.slots,
      this.cookSessions,
      this.planEntries,
      this.shoppingChecks,
      this.manualItems,
      this.shoppingRecipes,
      this.prefs,
      this.members,
      this.meta,
    ];
  }
}

let instance: Mirror | undefined;

export function mirror(): Mirror {
  instance ??= new Mirror();
  return instance;
}

/** Bumped on every applied change: the offline search index rebuilds when it moved. */
let version = 0;
export const mirrorVersion = () => version;

async function clearTables(db: Mirror): Promise<void> {
  await Promise.all(db.synced.filter((t) => t !== db.meta).map((t) => t.clear()));
  await db.meta.delete('cursor');
}

export async function readCursor(db: Mirror): Promise<number> {
  const row = await db.meta.get('cursor');
  return typeof row?.value === 'number' ? row.value : 0;
}

/**
 * The mirror belongs to one user of one household: a different sign-in on this device starts
 * over instead of seeing (or mixing in) someone else's recipes.
 */
export async function claimMirror(db: Mirror, owner: string): Promise<void> {
  const row = await db.meta.get('owner');
  if (row?.value === owner) return;
  await db.transaction('rw', [...db.synced, db.outbox, db.photos], async () => {
    await clearTables(db);
    // Another person's unsent changes don't belong to this sign-in.
    await db.outbox.clear();
    await db.photos.clear();
    await db.meta.put({ key: 'owner', value: owner });
  });
  version++;
}

/** Empties the mirror (sign-out). */
export async function clearMirror(db: Mirror = mirror()): Promise<void> {
  await db.transaction('rw', [...db.synced, db.outbox, db.photos], async () => {
    await clearTables(db);
    await db.outbox.clear();
    await db.photos.clear();
    await db.meta.delete('owner');
  });
  version++;
}

/**
 * Applies one pull atomically: upserts, tombstones and the new cursor together, then the changes
 * still in the outbox on top (the pulled rows don't have them yet).
 */
export async function applyChanges(db: Mirror, changes: SyncResponse): Promise<void> {
  await db.transaction('rw', [...db.synced, db.outbox], async () => {
    if (changes.reset) {
      await clearTables(db);
      return;
    }
    const { deleted } = changes;
    await db.recipes.bulkPut(changes.recipes);
    await db.lines.bulkPut(changes.lines);
    await db.ingredients.bulkPut(changes.ingredients);
    await db.slots.bulkPut(changes.slots);
    await db.cookSessions.bulkPut(changes.cookSessions);
    await db.planEntries.bulkPut(changes.planEntries);
    await db.shoppingChecks.bulkPut(changes.shoppingChecks);
    await db.manualItems.bulkPut(changes.manualItems);
    await db.shoppingRecipes.bulkPut(changes.shoppingRecipes);
    await db.members.bulkPut(changes.members);
    await db.prefs.bulkPut(changes.shoppingPrefs.map((p) => ({ ...p, key: 'shopping' as const })));
    await db.recipes.bulkDelete(deleted.recipes);
    await db.lines.bulkDelete(deleted.lines);
    await db.ingredients.bulkDelete(deleted.ingredients);
    await db.slots.bulkDelete(deleted.slots);
    await db.cookSessions.bulkDelete(deleted.cookSessions);
    await db.planEntries.bulkDelete(deleted.planEntries);
    await db.shoppingChecks.bulkDelete(deleted.shoppingChecks);
    await db.manualItems.bulkDelete(deleted.manualItems);
    await db.shoppingRecipes.bulkDelete(deleted.shoppingRecipes);
    await db.members.bulkDelete(deleted.members);
    // A deleted recipe's lines come as tombstones too; this catches any left behind.
    if (changes.deleted.recipes.length > 0) {
      await db.lines.where('recipeId').anyOf(changes.deleted.recipes).delete();
    }
    await db.meta.put({ key: 'cursor', value: changes.cursor });
    await rebaseOutbox(db);
  });
  version++;
}

export async function hasMirror(db: Mirror): Promise<boolean> {
  return (await db.meta.get('cursor')) !== undefined;
}

const byPosition = (a: SyncLine, b: SyncLine) => a.position - b.position;

async function catalogFor(db: Mirror, lines: SyncLine[]) {
  const ids = [...new Set(lines.map((l) => l.ingredientId).filter((id) => id !== null))];
  const entries = await db.ingredients.bulkGet(ids);
  return entries.filter((e) => e !== undefined);
}

/** All mirrored recipes as list cards, newest first (the server's default order). */
export async function localSummaries(db: Mirror): Promise<RecipeSummary[]> {
  const [recipes, lines] = await Promise.all([db.recipes.toArray(), db.lines.toArray()]);
  const catalog = new Map((await catalogFor(db, lines)).map((c) => [c.id, c]));
  const byRecipe = new Map<string, SyncLine[]>();
  for (const line of lines) {
    byRecipe.set(line.recipeId, [...(byRecipe.get(line.recipeId) ?? []), line]);
  }
  return recipes
    .map((r) => summarizeRecipe(r, byRecipe.get(r.id) ?? [], catalog))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** One mirrored recipe in the API's shape, or null. */
export async function localRecipe(db: Mirror, id: string): Promise<Recipe | null> {
  const recipe = await db.recipes.get(id);
  if (!recipe) return null;
  const lines = (await db.lines.where('recipeId').equals(id).toArray()).sort(byPosition);
  return {
    ...recipe,
    ingredients: lines.map(({ recipeId: _, ...line }) => line),
    catalog: await catalogFor(db, lines),
  };
}
